import type { components } from "../../api/schema";
import { useNavigate } from "@tanstack/react-router";
import { InlineError, Menu, Stack, type MenuItem } from "../../ui";
import { RowConfirm } from "../shared/RowConfirm";

type ScenarioResponse = components["schemas"]["ScenarioResponse"];

export interface ScenarioActionHandlers {
  deleteError: string | null;
  deleteScenarioId: string | null;
  isDeletePending: boolean;
  onCancelDelete: () => void;
  onConfirmDelete: (scenario: ScenarioResponse) => void;
  onOpenEdit: (scenarioId: string) => void;
  onStartDelete: (scenario: ScenarioResponse) => void;
}

export interface ScenarioActionsProps {
  handlers: ScenarioActionHandlers;
  scenario: ScenarioResponse;
}

export function ScenarioActions({ handlers, scenario }: ScenarioActionsProps) {
  const navigate = useNavigate();
  const isConfirming = handlers.deleteScenarioId === scenario.id;
  const confirm = (
    <RowConfirm
      confirmLabel="Confirm delete"
      isConfirming={isConfirming}
      isPending={isConfirming && handlers.isDeletePending}
      isTriggerDisabled={handlers.isDeletePending}
      onCancel={handlers.onCancelDelete}
      onConfirm={() => {
        handlers.onConfirmDelete(scenario);
      }}
      onStart={() => {
        handlers.onStartDelete(scenario);
      }}
      pendingLabel="Deleting..."
      sentence={`Delete ${scenario.display_name}? This removes the scenario from its agent.`}
      triggerLabel="Delete"
    />
  );
  if (isConfirming) {
    return (
      <Stack gap={2}>
        {confirm}
        {handlers.deleteError ? (
          <InlineError>{handlers.deleteError}</InlineError>
        ) : null}
      </Stack>
    );
  }
  const items: MenuItem[] = [
    {
      id: "view-edit",
      label:
        scenario.current_revision.mode === "scripted"
          ? "View scenario"
          : "Edit scenario",
      onSelect: () => {
        handlers.onOpenEdit(scenario.id);
      },
    },
    {
      id: "edit-rows",
      label: "Manage scenario rows",
      onSelect: () => {
        void navigate({
          params: { scenarioId: scenario.id },
          search: (previous) => previous,
          to: "/scenarios/$scenarioId/rows",
        });
      },
    },
    {
      danger: true,
      disabled: handlers.isDeletePending,
      id: "delete",
      label: "Delete",
      onSelect: () => {
        handlers.onStartDelete(scenario);
      },
    },
  ];

  return <Menu items={items} label="Actions" />;
}
